import { action, computed, observable, makeAutoObservable } from 'mobx';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import {
    PromotedEventsState,
    CompetitionCombinedType,
} from 'src/AppState/CmsState/PromotedEventsState/PromotedEventsState';
import { PromotedEventCompetitionState } from './PromotedEventCompetitionState';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getAllPromotedSports';

export type PromotedSportDataModelType = Response200Type[number];

export class PromotedEventSportState {
    private readonly promotedEventCompetitionStateMap: MobxMapAutoNew<number, PromotedEventCompetitionState>;

    @observable isOpenSection: boolean = false;
    @observable isActive: boolean = false;
    @observable showDeletePopup: boolean = false;

    constructor(
        private readonly trpcClient: TrpcClient,
        private readonly sportSlug: string,
        private readonly getSportData: (sportSlug: string) => PromotedSportDataModelType | null,
        private readonly promotedEventsSectionState: PromotedEventsState
    ) {
        this.isActive = this.getSportData(this.sportSlug)?.is_active ?? false;
        this.promotedEventCompetitionStateMap = new MobxMapAutoNew(
            (competitionId: number): PromotedEventCompetitionState =>
                new PromotedEventCompetitionState(
                    trpcClient,
                    competitionId,
                    this.getCompetitionData,
                    this.handleDeleteSport,
                    promotedEventsSectionState
                )
        );
        makeAutoObservable(this);
    }

    @computed get sportData(): PromotedSportDataModelType | null {
        return this.getSportData(this.sportSlug) ?? null;
    }

    @action public getCompetitionData = (competitionId: number): CompetitionCombinedType | null => {
        return (
            this.sportData?.competitions_list.find((competition) => competition.competition_id === competitionId) ??
            null
        );
    };

    getPromotedEventCompStateByCompId = (competitionId: number): PromotedEventCompetitionState => {
        return this.promotedEventCompetitionStateMap.get(competitionId);
    };

    @action public setSportActive = async (): Promise<void> => {
        if (this.sportData !== null) {
            const item = this.getSportData(this.sportSlug);

            if (item !== null) {
                const promotedSportDetailsData = {
                    ...item,
                    is_active: !item.is_active,
                };
                this.isActive = !this.isActive;

                try {
                    await this.trpcClient.client.promotedSports.updatePromotedSport.mutate({
                        data: promotedSportDetailsData,
                        id: this.sportData.id,
                    });

                    await this.promotedEventsSectionState.promotedEventsResource.refresh();
                    this.promotedEventsSectionState.setSportsDataFromResource();
                } catch (error) {
                    console.error(error);
                }
            } else {
                console.error('Cannot find the sport with provided ID.');
            }
        }
    };

    @action public handleToggleOpenSection = (): void => {
        this.isOpenSection = !this.isOpenSection;
    };

    @action handleHideDeletePopup = (): void => {
        this.showDeletePopup = false;
    };

    @action handleShowDeletePopup = (): void => {
        this.showDeletePopup = true;
    };

    @action public handleDeleteSport = async (): Promise<void> => {
        if (this.sportData !== null) {
            const removeResult = await this.trpcClient.client.promotedSports.deletePromotedSport.mutate({
                id: this.sportData.id,
            });

            if (removeResult.status === 200) {
                await this.handleDeleteAllCompetitions();
                await this.promotedEventsSectionState.promotedEventsResource.refresh();
                this.promotedEventsSectionState.setSportsDataFromResource();
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };

    @action public handleDeleteAllCompetitions = async (): Promise<void> => {
        const competitions = this.sportData?.competitions_list ?? [];
        const competitionIds = competitions.map((competition) => competition.id);

        for (const competition of competitions) {
            if (competition !== undefined) {
                const eventIds = competition.events_list.map((e) => e.id);
                await this.handleDeleteAllEvents(eventIds);
            }
        }

        for (const id of competitionIds) {
            const removeResult = await this.trpcClient.client.promotedCompetitions.deletePromotedCompetition.mutate({
                id,
            });

            if (removeResult.status === 200) {
                await this.promotedEventsSectionState.promotedEventsResource.refresh();
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };

    @action public handleDeleteAllEvents = async (eventsIds: Array<number>): Promise<void> => {
        for (const id of eventsIds) {
            const removeResult = await this.trpcClient.client.promotedEvents.deletePromotedEvent.mutate({ id });

            if (removeResult.status === 200) {
                await this.promotedEventsSectionState.promotedEventsResource.refresh();
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };
}
